import React, { useState, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, X, Download, Copy, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const QRCodeShare = ({ entries, variant }) => {
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate share URL
  const shareUrl = useMemo(() => {
    if (entries.length === 0) return window.location.href;
    const enabledEntries = entries.filter(e => e.enabled !== false);
    const entriesParam = enabledEntries.map(e => encodeURIComponent(e.text)).join(',');
    return `${window.location.origin}${window.location.pathname}?entries=${entriesParam}`;
  }, [entries]);

  // Copy URL to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Download QR code as PNG
  const handleDownload = () => {
    const svg = document.getElementById('wheel-qr-code');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `wheel-qr-${variant}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="btn-action-bar"
        aria-label={t('wheel.qrCode')}
        title={t('wheel.shareViaQR')}
        data-testid="qr-code-button"
      >
        <QrCode size={20} />
        <span>{t('wheel.qrCode')}</span>
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal qr-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
            
            <h2>📱 {t('wheel.shareViaQR')}</h2>
            
            <div className="qr-code-container">
              <QRCodeSVG
                id="wheel-qr-code"
                value={shareUrl}
                size={200}
                level="M"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>

            <div className="qr-url">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="qr-url-input"
              />
            </div>

            <div className="qr-actions">
              <button onClick={handleCopy} className="btn-qr-action">
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? t('wheel.copied') : t('wheel.copyUrl')}
              </button>
              <button onClick={handleDownload} className="btn-qr-action primary">
                <Download size={16} />
                {t('wheel.downloadQR')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QRCodeShare;
